import { useEffect, useState } from "react";
import { getUserLocationAPI } from "../../api/config/getUserLocation";

// The user's approximate location (city-level, from their IP via the Radar
// geocode endpoint already used for the phone country — no permission prompt),
// for searches app-backend makes to Google on the user's behalf: Google biases
// text search to the caller, and that caller is app-backend in us-east-1.
//
// Its own localStorage key, NOT the shared `userLocation` global — nothing has
// written that since the app-init call was removed (Feb 2026), and it feeds
// other payloads. Refreshed after MAX_AGE_MS because people travel.

export interface ApproxLocation {
  lat: number;
  lng: number;
}

const KEY = "_zenvoya_geo_point";
const MAX_AGE_MS = 6 * 60 * 60 * 1000;

const isPoint = (lat: unknown, lng: unknown): boolean =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  Math.abs(lat) <= 90 &&
  Math.abs(lng) <= 180;

export function getCachedApproxLocation(): ApproxLocation | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(KEY) || "null");
    if (!parsed || !isPoint(parsed.lat, parsed.lng)) return null;
    if (typeof parsed.ts !== "number" || Date.now() - parsed.ts > MAX_AGE_MS) return null;
    return { lat: parsed.lat, lng: parsed.lng };
  } catch {
    return null;
  }
}

/** Caches the point from a Radar geocode/ip response, if it has one. */
export function cacheApproxLocationFromRadar(data: any): ApproxLocation | null {
  const lat = Number(data?.address?.latitude);
  const lng = Number(data?.address?.longitude);
  if (!isPoint(lat, lng)) return null;
  try {
    localStorage.setItem(KEY, JSON.stringify({ lat, lng, ts: Date.now() }));
  } catch {
    /* private mode / quota — the point still serves this call */
  }
  return { lat, lng };
}

// One Radar call at a time per tab, however many fields ask.
let inFlight: Promise<ApproxLocation | null> | null = null;

export function fetchApproxLocation(accessJWT: string): Promise<ApproxLocation | null> {
  const cached = getCachedApproxLocation();
  if (cached) return Promise.resolve(cached);
  if (!inFlight) {
    inFlight = getUserLocationAPI(accessJWT)
      .then((data) => cacheApproxLocationFromRadar(data))
      .catch(() => null)
      .finally(() => {
        inFlight = null;
      });
  }
  return inFlight;
}

/**
 * The cached point, fetched once when missing or stale. Null until known (or
 * when Radar has nothing) — callers then simply send no location.
 */
export function useApproxLocation(accessJWT: string, enabled: boolean): ApproxLocation | null {
  const [point, setPoint] = useState<ApproxLocation | null>(() => (enabled ? getCachedApproxLocation() : null));
  useEffect(() => {
    if (!enabled || !accessJWT) return;
    let cancelled = false;
    fetchApproxLocation(accessJWT).then((p) => {
      if (!cancelled && p) setPoint((prev) => (prev && prev.lat === p.lat && prev.lng === p.lng ? prev : p));
    });
    return () => {
      cancelled = true;
    };
  }, [accessJWT, enabled]);
  return point;
}
